import searchIcon from '@jetbrains/icons/search.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Input from '@jetbrains/ring-ui/components/input/input'
import {useContext} from 'react'

import type {BuildId} from '../../../../../../types'
import {isKey} from '../../../../../../utils/guards'
import {cmdOrCtrlPlus} from '../../../../../../utils/keys'
import {
  PipelinesTarget,
  PipelinesTargetContext,
} from '../../../../../pages/PipelinesPages/contexts/targetContext'
import {buildLogSearchHint} from '../../../../Hints/HintItems/Hints.buildOverview'
import {stringifyHintId} from '../../../../Hints/Hints.types'
import BuildLogFilterMenu, {filterLabels} from '../../BuildLogFilterMenu/BuildLogFilterMenu'
import useBuildLogControls from '../../BuildLogHeader/BuildLogControls/BuildLogControls.hooks'

import DownloadBuildLogDropdown from './DownloadBuildLogDropdown/DownloadBuildLogDropdown'
import ViewToggle from './ViewToggle/ViewToggle'

import styles from './BuildLogControls.css'

type Props = {
  buildId: BuildId
  setSearchActive: (searchActive: boolean) => unknown
}

export default function BuildLogControls({buildId, setSearchActive}: Props) {
  const pipelinesTarget = useContext(PipelinesTargetContext)
  const {
    filterValue,
    searchEnabled,
    messagesExists,
    showSearch,
    onExpandAllClick,
    onCollapseAllClick,
  } = useBuildLogControls(setSearchActive)

  return (
    <div className={styles.controls}>
      {pipelinesTarget === PipelinesTarget.JOB_DETAILS_POPUP && (
        <>
          <Button
            onClick={onExpandAllClick}
            disabled={!messagesExists}
            data-test-full-build-log={'expand'}
          >
            {'Expand All'}
          </Button>
          <Button
            onClick={onCollapseAllClick}
            disabled={!messagesExists}
            data-test-full-build-log={'collapse'}
          >
            {'Collapse All'}
          </Button>
        </>
      )}

      {searchEnabled && (
        <Input
          className={styles.searchContainer}
          inputClassName={styles.search}
          data-hint-container-id={stringifyHintId(buildLogSearchHint.id)}
          onFocus={showSearch}
          disabled={!messagesExists}
          data-test-full-build-log={'open-search'}
          type="search"
          placeholder={`Search (${cmdOrCtrlPlus}F)`}
          icon={searchIcon}
        />
      )}
      <Dropdown
        anchor={
          <Button disabled={!messagesExists} data-test-full-build-log={'filter'} dropdown>
            {`${
              filterValue && isKey(filterLabels, filterValue)
                ? filterLabels[filterValue]
                : filterLabels.all
            } `}
          </Button>
        }
      >
        <BuildLogFilterMenu />
      </Dropdown>
      <ViewToggle />
      <DownloadBuildLogDropdown buildId={buildId} />
    </div>
  )
}
